import { useState, useEffect, createContext } from "react";
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import AccountMenu from "examples/AccountMenu";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import routes from "routes";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator, setOpenAccountMenu } from "context";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import ResetPassword from "layouts/authentication/reset-password";
import MobileBottomNav from "components/MobileBottomNav";
import useMediaQuery from "@mui/material/useMediaQuery";

import { getAuth, onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";

// Configuração do Firebase
const auth = getAuth();
const db = getFirestore();

// Criando e exportando o contexto de autenticação
export const AuthContext = createContext();

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    openAccountMenu,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  // Estado para o papel do usuário e dados de login
  const [userRole, setUserRole] = useState(localStorage.getItem("userRole"));
  const [user, setUser] = useState(auth.currentUser);

  // Função de login
  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      // Recupera o papel do usuário no Firestore
      const userDoc = await getDoc(doc(db, "usuarios", uid));
      if (userDoc.exists()) {
        setUserRole(userDoc.data().role);
        setUser(userCredential.user);
        localStorage.setItem("userRole", userDoc.data().role);
      } else {
        console.error("Usuário encontrado, mas sem documento no Firestore.");
      }
    } catch (error) {
      console.error("Erro ao fazer login:", error.message);
    }
  }

  const isMobile = useMediaQuery("(max-width: 600px)");

  // Listener para detectar mudanças no estado de autenticação
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const uid = currentUser.uid;

        try {
          const userDoc = await getDoc(doc(db, "usuarios", uid));
          if (userDoc.exists()) {
            setUserRole(userDoc.data().role);
            setUser(currentUser);
            localStorage.setItem("userRole", userDoc.data().role);
          }
        } catch (error) {
          console.error("Erro ao recuperar os dados do usuário:", error);
        }
      } else {
        setUser(null);
        setUserRole(null);
        localStorage.removeItem("userRole");
      }
    });

    return () => unsubscribe();
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const isAuthRoute = ["/authentication/sign-in", "/authentication/sign-up", "/authentication/reset-password"].includes(
    pathname
  );

  const getRoutes = (allRoutes) =>
    allRoutes
      .filter((route) => route.allowedRoles.includes(userRole))
      .map((route) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }
        if (route.route) {
          return <Route exact path={route.route} element={route.component} key={route.key} />;
        }
        return null;
      });

  useEffect(() => {
    // Configurações globais para evitar tela azul de fundo
    const root = document.documentElement;

    root.style.setProperty("height", "100%");
    root.style.setProperty("overscroll-behavior", "none"); // Evita overscroll
    root.style.setProperty("background-color", darkMode ? "#000" : "#f8f9fa");

    document.body.style.height = "100%";
    document.body.style.margin = "0";
    document.body.style.overscrollBehavior = "none"; // Impede overscroll
    document.body.style.touchAction = "pan-y"; // Permite apenas rolagem vertical
    document.body.style.overflowX = "hidden"; // Remove rolagem horizontal
    document.body.style.overflowY = "auto"; // Permite rolagem apenas vertical
    document.body.style.backgroundColor = darkMode ? "#000" : "#f8f9fa"; // Define a cor de fundo

    return () => {
      // Reseta estilos ao desmontar o componente
      root.style.removeProperty("height");
      root.style.removeProperty("overscroll-behavior");
      root.style.removeProperty("background-color");

      document.body.style.height = "";
      document.body.style.margin = "";
      document.body.style.overscrollBehavior = "";
      document.body.style.touchAction = "";
      document.body.style.overflowX = "";
      document.body.style.overflowY = "";
      document.body.style.backgroundColor = "";
    };
  }, [darkMode]);

  return (
    <AuthContext.Provider value={{ userRole, user, login }}>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />

        {!isAuthRoute && layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Finnoplan"
              routes={routes.filter((route) => route.allowedRoles.includes(userRole))}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            <AccountMenu />
          </>
        )}

        {!isAuthRoute && layout === "dashboard" && isMobile && <MobileBottomNav />}
        <Routes>
          <Route path="/authentication/sign-in" element={<SignIn />} />
          <Route path="/authentication/sign-up" element={<SignUp />} />
          <Route path="/authentication/reset-password" element={<ResetPassword />} />
          {getRoutes(routes)}
          <Route path="*" element={user ? <Navigate to="/dashboard" /> : <Navigate to="/authentication/sign-in" />} />
        </Routes>
      </ThemeProvider>
    </AuthContext.Provider>
  );
}
